import React, { useState } from 'react'
import type { CSSProperties } from 'react'
import type { SingleHorizontalBarProps } from './BarChart.types'
import {
  BAR_CHART_DEFAULT_TITLE,
  BAR_CHART_DEFAULT_VALUE_LABEL,
  BAR_CHART_HORIZONTAL_BAR_HEIGHT,
  BAR_CHART_HORIZONTAL_SINGLE_COLOR,
  BAR_CHART_HORIZONTAL_TRACK_GROUP_WIDTH,
} from './BarChart.types'
import { ChartTooltip } from '../ChartTooltip'
import './BarChart.css'

const clampPercent = (value: number): number =>
  Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0))

const SingleHorizontalBar: React.FC<SingleHorizontalBarProps> = ({
  title = BAR_CHART_DEFAULT_TITLE,
  hideTitle = false,
  valueLabel = BAR_CHART_DEFAULT_VALUE_LABEL,
  hideValue = false,
  barHeight = BAR_CHART_HORIZONTAL_BAR_HEIGHT,
  trackGroupWidth = BAR_CHART_HORIZONTAL_TRACK_GROUP_WIDTH,
  color = BAR_CHART_HORIZONTAL_SINGLE_COLOR,
  value = 100,
  showTooltip = true,
  formatValue,
  tooltipLabel = 'Value',
  darkMode = false,
  className = '',
  style,
  'aria-label': ariaLabel,
}) => {
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null)
  const fillPercent = clampPercent(value)
  const format = formatValue ?? ((v: number) => String(v))

  const classNames = [
    'ds-bar-chart',
    'ds-bar-chart--horizontal',
    'ds-bar-chart--single-horizontal',
    showTooltip ? 'ds-bar-chart--interactive' : '',
    darkMode ? 'ds-bar-chart--dark' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const rootStyle = {
    ...style,
    '--ds-bar-chart-bar-height': `${barHeight}px`,
    '--ds-bar-chart-track-group-width': `${trackGroupWidth}px`,
    '--ds-bar-chart-color': color,
    '--ds-bar-chart-fill': `${fillPercent}%`,
  } as CSSProperties

  const accessibleLabel = [title, valueLabel].filter(Boolean).join(', ')

  return (
    <div
      className={classNames}
      style={rootStyle}
      role="img"
      aria-label={ariaLabel ?? accessibleLabel}
      onMouseEnter={
        showTooltip
          ? (event) => {
              const rect = event.currentTarget.getBoundingClientRect()
              setHover({ x: Math.min(rect.width / 2, 120), y: 4 })
            }
          : undefined
      }
      onMouseLeave={showTooltip ? () => setHover(null) : undefined}
    >
      {!hideTitle ? <p className="ds-bar-chart__title">{title}</p> : null}
      <div className="ds-bar-chart__track-group">
        <div className="ds-bar-chart__bar" aria-hidden="true">
          <div
            className={
              fillPercent >= 100
                ? 'ds-bar-chart__fill'
                : 'ds-bar-chart__fill ds-bar-chart__fill--valued'
            }
          />
        </div>
        {!hideValue ? (
          <p className="ds-bar-chart__value">{valueLabel}</p>
        ) : null}
      </div>
      {showTooltip && hover ? (
        <ChartTooltip
          visible
          darkMode={darkMode}
          title={title}
          x={hover.x}
          y={hover.y}
          items={[
            {
              label: tooltipLabel,
              value: valueLabel || format(fillPercent),
              color,
            },
          ]}
        />
      ) : null}
    </div>
  )
}

export default SingleHorizontalBar
